import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import Axios from 'axios';
import Login from './pages/login/Login';
import { useCallback, useEffect, useState } from 'react';
import Sidenav from './components/layout/sidenav';
import Banner from './components/layout/banner';
import HomePage from './pages/HomePage/HomePage';
import AddMedPage from './pages/addMed/AddMedPage';
import MedDetailPage from './pages/medDetail/MedDetailPage';
import SearchMed from './pages/medSearch/MedSearch';
import SendPedigree from './pages/sendPedigree/SendPedigree.tsx';
import ReportAnIncident from './pages/reportAnIncident/reportAnIncident';
import IncidentTracker from './pages/incidentTracker/incidentTracker';
import SettingsPage from './pages/settings/SettingsPage';
import OrderUpload from './pages/orders/orderUpload';
import OrderRouter from './pages/orders/orderRouter';
import DemoEpcisFileGenerator from './pages/demoEpcisFileGenerator/DemoEpcisFileGenerator';
import TradingPartnerRouter from './pages/tradingPartners/TradingPartnerRouter.tsx';
import GlobalLocationNumbersRouter from './pages/globalLocationNumbers/globalLocationNumbersRouter.tsx';
import api from './common/api/index.ts';
import { useQuery } from '@tanstack/react-query';
import SubscriptionRouter from './pages/subscription/SubscriptionRouter.tsx';
import endpoints from './common/endpoints.ts';
import ManageLocationsRouter from './pages/manageLocations/ManageLocationsRouter.tsx';
import ManageUsersRouter from './pages/manageUsers/ManageUsersRouter.tsx';
import RecallRouter from './pages/recalls/RecallRouter.tsx';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import DocumentExceptions from './pages/documentExceptions/documentExceptions.js';
import DocumentExceptionDetails from './pages/documentExceptions/documentExceptionDetails.js';
import SentEpcisDocumentsRouter from './pages/sentEpcisDocuments/SentEpcisDocumentsRouter.tsx';
import LimitedAccessRouter from './limitedAccessPages/LimitedAccessRouter.tsx';
import SopUpload from './pages/SopUpload/SopUpload.js';
import DeactivateLocationModal from './pages/manageLocations/modals/DeactivateLocationModal.tsx';
import AmendEpcis from './pages/amendEpcis/AmendEpcis.tsx';
import QuarantineMeds from './pages/quarantineMeds/QuarantineMeds.tsx';
import ActAsUserModal from './components/ActAsUserModal.tsx';
import UniversalScanner from './components/UniversalScanner.tsx';
import useCurrentCustomerLocationId from './hooks/useCurrentCustomerLocation.ts';
import CustomerLocationSelect from './components/CustomerLocationDropdown.tsx';
import useActingAsId from './hooks/useActingAsId.ts';

const medtraxAuthDataKey = 'auth_data';
const excludedPathPrefixesForScanner = [
  '/orders',
  '/sendapedigree',
  '/quarantine',
];
const excludedPathPrefixesForLocationSelect = [
  '/users',
  '/locations',
  '/subscription',
  '/settings/changepassword',
  '/settings/othersettings',
];

const App = () => {
  return (
    <>
      <BrowserRouter>
        <Applet />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

const Applet = () => {
  const isLoggedIn = () => {
    return !!localStorage.getItem(medtraxAuthDataKey);
  };

  const { pathname } = useLocation();
  const { actingAsId } = useActingAsId();
  const { customerLocationId, setCustomerLocationId } =
    useCurrentCustomerLocationId();
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [authLoading, setAuthLoading] = useState(true);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isOpenActAsUserModal, setIsOpenActAsUserModal] = useState(false);

  const logout = () => {
    localStorage.removeItem(medtraxAuthDataKey);
    setLoggedIn(false);
  };

  const getUserType = () => {
    const fromStorage = localStorage.getItem(medtraxAuthDataKey);
    if (!fromStorage) return undefined;
    const { _user_type } = JSON.parse(fromStorage);
    return _user_type || undefined;
  };
  const [userType, setUserType] = useState(getUserType());

  const customerLocationOnChange = useCallback(
    e => {
      const { value } = e.target;
      setCustomerLocationId(value);
    },
    [setCustomerLocationId]
  );

  const { data: warnings } = useQuery({
    enabled: loggedIn && userType === 'customer',
    queryKey: [endpoints.illegitReport.read.count.by.userId()],
    queryFn: api.illegitReport.read.count.by.userId,
  });

  const { data: customerLocations, isLoading: isLoadingCustomerLocations } =
    useQuery({
      enabled: loggedIn && userType !== 'trading partner',
      queryKey: [endpoints.customerLocation.read.all.by.userId(), actingAsId],
      queryFn: api.customerLocation.read.all.by.userId,
    });

  const shouldShowComponent = useCallback(
    excludedPaths => {
      return !excludedPaths.some(excludedPath =>
        pathname.startsWith(excludedPath)
      );
    },
    [pathname]
  );

  const LocationSelect = useCallback(() => {
    if (!isLoggedIn) return null;
    if (userType === 'trading partner') return null;
    if (isLoadingCustomerLocations) return null;
    if (!shouldShowComponent(excludedPathPrefixesForLocationSelect))
      return null;

    if (!customerLocations?.length)
      return (
        <span
          className="input-field col s3 right"
          style={{
            marginTop: '25px',
            marginRight: '20px',
            width: '330px',
            padding: '10px',
            paddingTop: '0',
            color: 'red',
          }}
        >
          No Available Customer Locations
        </span>
      );

    return (
      <CustomerLocationSelect
        customerLocations={customerLocations}
        customerLocationId={customerLocationId}
        customerLocationOnChange={customerLocationOnChange}
      />
    );
  }, [
    userType,
    customerLocations,
    isLoadingCustomerLocations,
    customerLocationId,
    customerLocationOnChange,
    shouldShowComponent,
  ]);

  useEffect(() => {
    if (!customerLocations?.length) {
      return;
    }

    const locationExists = customerLocations.some(
      location => Number(location.id) === Number(customerLocationId)
    );

    if (!locationExists || !customerLocationId) {
      setCustomerLocationId(customerLocations[0].id);
    }
  }, [customerLocations, customerLocationId, setCustomerLocationId]);

  useEffect(() => {
    const authData = localStorage.getItem('auth_data');

    if (!authData) {
      setLoggedIn(false);
      setAuthLoading(false);
      return;
    }

    Axios.post('/api/v1/auth/update/one/token', JSON.parse(authData))
      .then(() => {
        setLoggedIn(true);
        setUserType(getUserType());
      })
      .catch(() => {
        localStorage.clear();
        setLoggedIn(false);
        setUserType(undefined);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  }, []);

  //Materialize css sometimes changes the body overflow to 'hidden'
  //this breaks the layout occasionally. this code runs on every
  //render to fix that. It runs a ton, but the impact is negligible
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (authLoading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <div className="preloader-wrapper active">
          <div className="spinner-layer spinner-blue-only">
            <div className="circle-clipper left">
              <div className="circle" />
            </div>
            <div className="gap-patch">
              <div className="circle" />
            </div>
            <div className="circle-clipper right">
              <div className="circle" />
            </div>
          </div>
        </div>
        <div>
          <span>Loading . . .</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {loggedIn ? (
        <>
          <Sidenav
            logout={logout}
            userType={userType}
            setIsOpenActAsUserModal={setIsOpenActAsUserModal}
          />
          <Banner />
          {LocationSelect()}
          <div className="offset-for-sidenav">
            {!['customer', 'employee'].includes(
              (userType ?? '').toLowerCase()
            ) ? (
              <LimitedAccessRouter />
            ) : (
              <>
                <Routes>
                  <Route path="/" element={<HomePage warnings={warnings} />} />
                  <Route path="/inputmeds" element={<AddMedPage />} />
                  <Route path="/meddetail/:medId" element={<MedDetailPage />} />
                  <Route path="/searchmeds" element={<SearchMed />} />
                  <Route path="/sendapedigree" element={<SendPedigree />} />

                  <Route
                    path="/sendapedigree/:serialNumber"
                    element={<SendPedigree />}
                  />

                  <Route
                    path="/reportanincident/:incidentId/:type"
                    element={<ReportAnIncident />}
                  />
                  <Route
                    path="/reportanincident"
                    element={<ReportAnIncident />}
                  />
                  <Route
                    path="/incidenttracker"
                    element={<IncidentTracker />}
                  />
                  <Route path="/exceptions" element={<DocumentExceptions />} />
                  <Route
                    path="/exceptions/:documentExceptionId"
                    element={<DocumentExceptionDetails />}
                  />
                  <Route path="/quarantine" element={<QuarantineMeds />} />

                  <Route path="/settings/*" element={<SettingsPage />} />
                  <Route path="/orders/*" element={<OrderRouter />} />
                  <Route path="/orderupload/*" element={<OrderUpload />} />
                  <Route
                    path="/tradingpartner/*"
                    element={<TradingPartnerRouter />}
                  />
                  <Route
                    path="/gln/*"
                    element={<GlobalLocationNumbersRouter />}
                  />
                  {process.env.REACT_APP_NODE_ENV !== 'production' && (
                    <Route
                      path="/demo/epcis/file/generator"
                      element={<DemoEpcisFileGenerator />}
                    />
                  )}
                  <Route
                    path="/subscription/*"
                    element={<SubscriptionRouter />}
                  />
                  <Route
                    path="/locations/*"
                    element={<ManageLocationsRouter />}
                  />
                  <Route path="/users/*" element={<ManageUsersRouter />} />
                  <Route path="/recalls/*" element={<RecallRouter customerLocationId={customerLocationId} />} />
                  <Route
                    path="/sentepcisdocs/*"
                    element={<SentEpcisDocumentsRouter />}
                  />

                  <Route path="/sop" element={<SopUpload />} />
                  <Route
                    path="/amend/:outboundDocumentId"
                    element={<AmendEpcis />}
                  />
                </Routes>
                <DeactivateLocationModal
                  setIsOpen={setShowDeactivateModal}
                  isOpen={showDeactivateModal}
                  userType={userType}
                />
                {userType === 'employee' ? (
                  <ActAsUserModal
                    isOpen={isOpenActAsUserModal}
                    setIsOpen={setIsOpenActAsUserModal}
                  />
                ) : null}
                {shouldShowComponent(excludedPathPrefixesForScanner) && (
                  <UniversalScanner />
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <Login setLoggedIn={setLoggedIn} setUserType={setUserType} />
      )}
    </>
  );
};

export default App;
